import { defineStore } from 'pinia'
import { hasOrgPermission } from '@/utils/orgPermissions'

export interface UserInfo {
  id: number
  username: string
  real_name: string | null
  pay_user_id: string | null
  email: string | null
  status: number
  is_super_admin: number
  /** 是否可查看算法任务敏感详情；旧接口未返回时按 0 */
  algorithm_detail_visible?: number
  /** 是否可访问运维大盘 */
  ops_dashboard_access?: number
  last_login_at: Date | null
  created_at: Date
}

export interface AdminUser {
  id: number
  username: string
  real_name: string | null
}

export interface OrgImpersonationInfo {
  org_id: number
  org_name: string | null
  member_id: number
  member_username: string | null
  member_real_name: string | null
  shop_id: number
  shop_name: string | null
  permissions: string[]
}

export interface AuthState {
  // 用户登录信息
  user: UserInfo | null
  isUserAuthenticated: boolean
  
  // 管理员代登状态
  impersonating: boolean
  adminUser: AdminUser | null

  // 组织代登状态
  orgImpersonating: boolean
  orgImpersonation: OrgImpersonationInfo | null
  
  // 千川 API token 信息
  accessToken: string | null
  refreshToken: string | null
  expiresAt: number | null
  advertiserId: number | null
  
  // 千川账号信息
  accountId: number | null
  accountName: string | null
  accountType: string | null
  accountInfo: any | null
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    isUserAuthenticated: false,
    impersonating: false,
    adminUser: null,
    orgImpersonating: false,
    orgImpersonation: null,
    accessToken: null,
    refreshToken: null,
    expiresAt: null,
    advertiserId: null,
    accountId: null,
    accountName: null,
    accountType: null,
    accountInfo: null
  }),

  getters: {
    // 用户是否已登录
    isAuthenticated: (state) => state.isUserAuthenticated && !!state.user,
    
    // 是否是超级管理员
    isSuperAdmin: (state) => state.user?.is_super_admin === 1,
    
    // 是否为付费渠道用户（有 pay_user_id 表示从付费中心来的）
    isPayUser: (state) => !!state.user?.pay_user_id,
    
    // 是否拥有管理员权限（本身是管理员 或 管理员代登状态；不含组织代登）
    hasAdminAccess: (state) => state.user?.is_super_admin === 1 || state.impersonating,

    hasOrgPerm: (state) => (perm: string) => {
      if (!state.orgImpersonating) return true
      return hasOrgPermission(state.orgImpersonation?.permissions || [], perm)
    },

    /** 算法任务页：规则详情列、详情抽屉、敏感字段（与接口 canViewAlgorithmTaskSensitiveDetails 一致，含代登） */
    canViewAlgorithmTaskSensitiveDetails: (state) =>
      state.user?.is_super_admin === 1 ||
      (state.user?.algorithm_detail_visible ?? 0) === 1 ||
      state.impersonating,

    /** 是否可访问运维大盘子站 */
    hasOpsDashboardAccess: (state) =>
      state.user?.is_super_admin === 1 ||
      (state.user?.ops_dashboard_access ?? 0) === 1 ||
      state.impersonating,
    
    // 千川 token 是否有效
    isTokenExpired: (state) => {
      if (!state.expiresAt) return true
      return Date.now() >= state.expiresAt
    },
    
    // 是否有千川授权（只要有accessToken就认为已授权）
    hasQianchuanAuth: (state) => !!state.accessToken,

    // 授权完整：token 有效且账号信息齐全
    hasCompleteQianchuanAuth: (state) => {
      if (!state.accessToken) return false
      return !!(state.accountId || state.accountName || state.accountInfo?.display_name)
    },

    // 展示用账号名（优先 accountName，回退 display_name）
    displayAccountName: (state) =>
      state.accountName || state.accountInfo?.display_name || null
  },

  actions: {
    // 设置用户信息
    setUser(user: UserInfo | null) {
      this.user = user
      this.isUserAuthenticated = !!user
    },

    // 清除用户登录状态
    clearUser() {
      this.user = null
      this.isUserAuthenticated = false
      this.impersonating = false
      this.adminUser = null
      this.orgImpersonating = false
      this.orgImpersonation = null
    },

    // 设置千川 token
    setAccessToken(token: string) {
      this.accessToken = token
    },

    setRefreshToken(token: string) {
      this.refreshToken = token
    },

    // 设置过期时间（接收毫秒时间戳）
    setExpiresAt(expiresAtTimestamp: number) {
      this.expiresAt = expiresAtTimestamp
    },

    setAdvertiserId(id: number | null) {
      this.advertiserId = id
    },

    setAccountInfo(accountId: number | null, accountName: string | null, accountType: string | null, accountInfo: any | null) {
      this.accountId = accountId
      this.accountName = accountName
      this.accountType = accountType
      this.accountInfo = accountInfo
    },

    // 清除所有认证信息
    clearAuth() {
      this.clearUser()
      this.impersonating = false
      this.adminUser = null
      this.orgImpersonating = false
      this.orgImpersonation = null
      this.accessToken = null
      this.refreshToken = null
      this.expiresAt = null
      this.advertiserId = null
      this.accountId = null
      this.accountName = null
      this.accountType = null
      this.accountInfo = null
    },

    // 从服务端获取用户信息
    async fetchUserInfo() {
      try {
        const response = await $fetch('/api/auth/user-me') as any
        if (response.authenticated && response.user) {
          // 转换日期字符串为 Date 对象
          const user = {
            ...response.user,
            last_login_at: response.user.last_login_at ? new Date(response.user.last_login_at) : null,
            created_at: new Date(response.user.created_at)
          }
          this.setUser(user as UserInfo)
          
          // 更新代登状态
          this.impersonating = !!response.impersonating
          this.adminUser = response.adminUser || null
          this.orgImpersonating = !!response.orgImpersonating
          this.orgImpersonation = response.orgImpersonation || null
        } else {
          this.clearUser()
        }
      } catch (error) {
        console.error('获取用户信息失败:', error)
        this.clearUser()
      }
    },

    // 从服务端获取千川认证状态
    async fetchAuthStatus() {
      try {
        const response = await $fetch('/api/auth/me') as any
        if (response.authenticated && response.accessToken) {
          this.setAccessToken(response.accessToken)
          this.setRefreshToken(response.refreshToken || null)
          // expiresAt 已经是毫秒时间戳，直接使用
          this.setExpiresAt(response.expiresAt)
          this.setAdvertiserId(response.advertiserId || null)
          this.setAccountInfo(
            response.accountId || null,
            response.accountName || null,
            response.accountType || null,
            response.accountInfo || null
          )
        } else {
          this.accessToken = null
          this.refreshToken = null
          this.expiresAt = null
          this.advertiserId = null
          this.accountId = null
          this.accountName = null
          this.accountType = null
          this.accountInfo = null
        }
      } catch (error) {
        console.error('获取认证状态失败:', error)
      }
    }
  }
})

